import axios from "axios";
import Config from "../../Config";

class AffiliateService {
  getHeaders() {
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      },
    };
  }

  async registerAffiliate(payload) {
    try {
      const response = await axios.post(
        `${Config.baseUrl}/affiliate/register`,
        payload,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data.data,
        message:
          response.data.data.reason || "Affiliate registered successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "Something went wrong",
      };
    }
  }

  async updateAffiliate(affiliate_code, payload) {
    try {
      const response = await axios.post(
        `${Config.baseUrl}/edit/affiliate/profile`,
        { ...payload, affiliate_code },
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data.data,
        message: response.data.data.reason || "Affiliate updated successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "Something went wrong",
      };
    }
  }

  async getAffiliates(
    page = 1,
    limit = 15,
    search = "",
    sortBy = "",
    sortOrder = "asc",
    dateFrom = "",
    dateTo = "",
    channel = "",
    segment = ""
  ) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(sortBy && { sort_by: sortBy }),
        ...(sortOrder && { sort_order: sortOrder }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        ...(channel && { channel }),
        ...(segment && { segment }),
      });
      const response = await axios.get(
        `${Config.baseUrl}/all/affiliates?${params}`,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data,
        message: response.data.data.reason || "Affiliates fetched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "Something went wrong",
      };
    }
  }

  async getAllAffiliateStats() {
    try {
      const response = await axios.get(
        `${Config.baseUrl}/get_affiliates_stats`,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data,
        message:
          response.data.data.reason || "Affiliate stats fetched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "Something went wrong",
      };
    }
  }

  async showAffiliateDetails(affiliate_code) {
    try {
      const response = await axios.get(
        `${Config.baseUrl}/show/affiliate/${affiliate_code}`,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data,
        message:
          response.data.reason || "Affiliate details fetched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "Something went wrong",
      };
    }
  }

  async updateAffiliateStatus(payload) {
    try {
      const response = await axios.post(
        `${Config.baseUrl}/updateStatus/affiliate`,
        payload,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data,
        message: response.data.data.reason || "Status updated successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error?.response?.data?.data?.reason ||
          "Sorry, status update could not be completed",
      };
    }
  }

  async updateAffiliateData(payload) {
    try {
      const response = await axios.post(
        `${Config.baseUrl}/update_affiliate_data`,
        payload,
        this.getHeaders()
      );
      return {
        status: response.data?.data?.status_code === "000",
        data: response.data?.data?.data || response.data?.data,
        message: response.data?.data?.reason || response.data?.data?.message || "Affiliate data updated successfully",
        statusCode: response.data?.data?.status_code,
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "Failed to update affiliate data",
      };
    }
  }

  async toggleAffiliateStatus(affiliate_code) {
    try {
      const payload = {
        affiliate_code,
      };
      const response = await axios.post(
        `${Config.baseUrl}/update_affiliate_status`,
        payload,
        this.getHeaders()
      );
      return {
        status: response.data?.data?.status_code === "000" || response.data?.status_code === "000",
        data: response.data?.data?.data || response.data?.data,
        message: response.data?.data?.reason || response.data?.data?.message || response.data?.message || "Affiliate status updated successfully",
        statusCode: response.data?.data?.status_code || response.data?.status_code,
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "Failed to update affiliate status",
      };
    }
  }

  // Keep activateAffiliate for backward compatibility (now calls toggle)
  async activateAffiliate(affiliate_code) {
    return this.toggleAffiliateStatus(affiliate_code);
  }

  async exportAffiliatesData(segment = "all", dateFrom = "", dateTo = "") {
    try {
      const payload = {
        segment,
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      };
      const response = await axios.post(
        `${Config.baseUrl}/export_affiliates_data`,
        payload,
        this.getHeaders()
      );
      return {
        status: response.data?.data?.status_code === "000" || response.data?.status_code === "000",
        data: response.data?.data || response.data,
        message: response.data?.data?.reason || response.data?.data?.message || response.data?.message || "Export file will be sent to your email shortly",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          "Failed to export data",
      };
    }
  }
}

const affiliateService = new AffiliateService();
export default affiliateService;
